// UserInfoQuestionDependenciesTab.js
import React from "react";
import { Row, Col, Button } from "reactstrap";
import Select from "react-select";
import AffectedAttributesList from "./AffectedAttributesList";
import AutoResizeInput from "../../components/AutoResizeInput";

const UserInfoQuestionDependencies = ({
  infoQuestion,
  setInfoQuestion,
  prevQuestions,
  selectedReferencePrevQuestion,
  handleRefQuestionChange,
  onSave,

  isSavingActivationConditionSets,
  setIsSavingActivationConditionSets,
  isSavedActivationConditionSets,
  setIsSavedActivationConditionSets,
  isSavingAffectedAttribute,
  setIsSavingAffectedAttribute,
  isSavedAffectedAttribute,
  setIsSavedAffectedAttribute,
  isSavingVerifyRuleAI,
  setIsSavingVerifyRuleAI,
  isSavedVerifyRuleAI,
  setIsSavedVerifyRuleAI,
}) => {

  //--------------------------------------------------
  // Managing activationConditionSets
  //--------------------------------------------------
  
  const handleAddActivationConditionSet = () => {
    setIsSavedActivationConditionSets(false);
    setInfoQuestion((prev) => ({
      ...prev,
      dependencies: {
        ...prev.dependencies,
        activationConditionSets: [
          ...prev.dependencies.activationConditionSets,
          { conditions: [""] },
        ],
      },
    }));
  };

  const handleRemoveActivationConditionSet = (groupIndex) => {
    setIsSavedActivationConditionSets(false);
    setInfoQuestion((prev) => {
      const updatedSets = [...prev.dependencies.activationConditionSets];
      updatedSets.splice(groupIndex, 1);
      return {
        ...prev,
        dependencies: {
          ...prev.dependencies,
          activationConditionSets: updatedSets,
        },
      };
    });
  };

  const handleAddCondition = (groupIndex) => {
    setIsSavedActivationConditionSets(false);
    setInfoQuestion((prev) => {
      const updatedSets = [...prev.dependencies.activationConditionSets];
      const newConditions = [...updatedSets[groupIndex].conditions, ""];
      updatedSets[groupIndex] = {
        ...updatedSets[groupIndex],
        conditions: newConditions,
      };
      return {
        ...prev,
        dependencies: {
          ...prev.dependencies,
          activationConditionSets: updatedSets,
        },
      };
    });
  };

  const handleConditionChange = (groupIndex, conditionIndex, newValue) => {
    setIsSavedActivationConditionSets(false);
    setInfoQuestion((prev) => {
      const updatedSets = [...prev.dependencies.activationConditionSets];
      const updatedConditions = [...updatedSets[groupIndex].conditions];
      updatedConditions[conditionIndex] = newValue;
      updatedSets[groupIndex] = {
        ...updatedSets[groupIndex],
        conditions: updatedConditions,
      };
      return {
        ...prev,
        dependencies: {
          ...prev.dependencies,
          activationConditionSets: updatedSets,
        },
      };
    });
  };

  const handleRemoveCondition = (groupIndex, conditionIndex) => {
    setIsSavedActivationConditionSets(false);
    setInfoQuestion((prev) => {
      const updatedSets = [...prev.dependencies.activationConditionSets];
      const updatedConditions = [...updatedSets[groupIndex].conditions];
      updatedConditions.splice(conditionIndex, 1);
      updatedSets[groupIndex] = {
        ...updatedSets[groupIndex],
        conditions: updatedConditions,
      };
      return {
        ...prev,
        dependencies: {
          ...prev.dependencies,
          activationConditionSets: updatedSets,
        },
      };
    });
  };

  const handleSaveActivationConditions = async () => {
    const activationConditionSets = infoQuestion.dependencies.activationConditionSets
      .map(setItem => {
        const filteredConditions = setItem.conditions
          .map(cond => cond.trim())
          .filter(cond => cond !== "");
        return { conditions: filteredConditions };
      })
      .filter(setItem => setItem.conditions.length > 0);

    const payload = {
      dependencies: {
        ...infoQuestion.dependencies,
        activationConditionSets,
      },
    };

    setIsSavingActivationConditionSets(true);

    try {
      await onSave(payload);
      setIsSavedActivationConditionSets(true);
    } catch (error) {
      // TODO: show error message
      console.error(error);
    } finally {
      setIsSavingActivationConditionSets(false);
    }
    
  };

  //--------------------------------------------------
  // Managing affectedAttributes
  //--------------------------------------------------
  const handleAddAffectedAttribute = () => {
    setIsSavedAffectedAttribute(false);
    setInfoQuestion((prev) => ({
      ...prev,
      dependencies: {
        ...prev.dependencies,
        affectedAttributes: [...prev.dependencies.affectedAttributes, ""],
      },
    }));
  };

  // Changing an item within "affectedAttributes"
  const handleAffectedAttributeChange = (index, newValue) => {
    setIsSavedAffectedAttribute(false);
    setInfoQuestion((prev) => {
      const updated = [...prev.dependencies.affectedAttributes];
      updated[index] = newValue;
      return {
        ...prev,
        dependencies: {
          ...prev.dependencies,
          affectedAttributes: updated,
        },
      };
    });
  };

  // The reorder function for "affectedAttributes"
  const handleReorderAffectedAttributes = (newOrder) => {
    setIsSavedAffectedAttribute(false);
    setInfoQuestion((prev) => ({
      ...prev,
      dependencies: {
        ...prev.dependencies,
        affectedAttributes: newOrder
      },
    }));
  };

  // Removing an item from "affectedAttributes"
  const handleRemoveAffectedAttribute = (index) => {
    setIsSavedAffectedAttribute(false);
    setInfoQuestion((prev) => {
      const updated = [...prev.dependencies.affectedAttributes];
      updated.splice(index, 1);
      return {
        ...prev,
        dependencies: {
          ...prev.dependencies,
          affectedAttributes: updated,
        },
      };
    });
  };

  // The "Save" function for "affectedAttributes"
  const handleSaveAffectedAttributes = async () => {
    // Filter out empty strings
    const filtered = infoQuestion.dependencies.affectedAttributes
      .map(attr => attr.trim())           // trim whitespace
      .filter(attr => attr !== "");       // remove empty

    const payload = {
      dependencies: {
        ...infoQuestion.dependencies,
        affectedAttributes: filtered,
      },
    };

    /*
    if (infoQuestion.dependencies.affectedAttributes.some(attr => !attr.trim())) {
      //alert("Please fill in all affected attributes or remove them before saving.");
      return;
    }
    */

    setIsSavingAffectedAttribute(true);

    try {
      await onSave(payload);
      setIsSavedAffectedAttribute(true);
    } catch (error) {
      // TODO: show error message
      console.error(error);
    } finally {
      setIsSavingAffectedAttribute(false);
    }
    
  };

  //--------------------------------------------------
  // Managing verifyRuleAI
  //--------------------------------------------------

  const handleReorderVerifyRuleAI = (newOrder) => {
    setIsSavedVerifyRuleAI(false);
    setInfoQuestion((prev) => ({
      ...prev,
      verifyRuleAI: newOrder,
    }));
  };

  // Add a new VerifyRuleAI group
  const handleAddVerifyRuleAISet = () => {
    setIsSavedVerifyRuleAI(false);
    setInfoQuestion((prev) => ({
      ...prev,
      verifyRuleAI: [
        ...prev.verifyRuleAI,
        { conditions: [""] },
      ],
    }));
  };

  // Remove a VerifyRuleAI group
  const handleRemoveVerifyRuleAISet = (groupIndex) => {
    setIsSavedVerifyRuleAI(false);
    setInfoQuestion((prev) => {
      const updated = [...prev.verifyRuleAI];
      updated.splice(groupIndex, 1);
      return { ...prev, verifyRuleAI: updated };
    });
  };

  // Add a condition inside a VerifyRuleAI group
  const handleAddVerifyRuleAICondition = (groupIndex) => {
    setIsSavedVerifyRuleAI(false);
    setInfoQuestion((prev) => {
      const updated = [...prev.verifyRuleAI];
      const newConditions = [...updated[groupIndex].conditions, ""];
      updated[groupIndex] = {
        ...updated[groupIndex],
        conditions: newConditions,
      };
      return { ...prev, verifyRuleAI: updated };
    });
  };

  // Change a condition inside a VerifyRuleAI group
  const handleVerifyRuleAIConditionChange = (groupIndex, conditionIndex, newValue) => {
    setIsSavedVerifyRuleAI(false);
    setInfoQuestion((prev) => {
      const updated = [...prev.verifyRuleAI];
      const updatedConditions = [...updated[groupIndex].conditions];
      updatedConditions[conditionIndex] = newValue;
      updated[groupIndex] = {
        ...updated[groupIndex],
        conditions: updatedConditions,
      };
      return { ...prev, verifyRuleAI: updated };
    });
  };

  // Remove a condition inside a VerifyRuleAI group
  const handleRemoveVerifyRuleAICondition = (groupIndex, conditionIndex) => {
    setIsSavedVerifyRuleAI(false);
    setInfoQuestion((prev) => {
      const updated = [...prev.verifyRuleAI];
      const updatedConditions = [...updated[groupIndex].conditions];
      updatedConditions.splice(conditionIndex, 1);
      updated[groupIndex] = {
        ...updated[groupIndex],
        conditions: updatedConditions,
      };
      return { ...prev, verifyRuleAI: updated };
    });
  };

  // Save verifyRuleAI
  const handleSaveVerifyRuleAI = async () => {
    // clean up empty strings
    const cleanedVerifyRuleAI = infoQuestion.verifyRuleAI
      .map(set => ({
        conditions: set.conditions.map(c => c.trim()).filter(c => c !== "")
      }))
      .filter(set => set.conditions.length > 0); // remove empty sets

    const payload = {
      verifyRuleAI: cleanedVerifyRuleAI,
    };

    setIsSavingVerifyRuleAI(true);

    try {
      await onSave(payload);
      setIsSavedVerifyRuleAI(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSavingVerifyRuleAI(false);
    }
  };

  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <Row>
      {/* --- ActivationConditionSets --- */}
      <Col xs={12} md={6} xl={4} className="mb-3 mb-xl-0">
        <Row>
          <Col
            xs={12}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            {/*<h3>Activation Condition</h3>*/}
            <h3>顯示條件</h3>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                color="outline-info"
                onClick={handleAddActivationConditionSet}
                style={{ padding: "7px 8px", marginRight: "3px" }}
              >
                <i className="bx bx-plus" />
              </Button>
              <Button
                color="outline-success"
                onClick={handleSaveActivationConditions}
                style={{ padding: "7px 8px" }}
              >
                <i className="bx bx-check" />
              </Button>
            </div>
          </Col>
        </Row>

        {/* Show status */}
        <Row>
          {isSavingActivationConditionSets && <span style={{ color: "#f1b34c" }}>Saving...</span>}
          {!isSavingActivationConditionSets && 
            <span style={{ color: isSavedActivationConditionSets ? "#34c38f" : "#aeabab" }}>✔ Saved!</span>
          }
        </Row>

        {infoQuestion.dependencies.activationConditionSets.length === 0 ? (
          <Row
            style={{ margin: "0.5rem 0", border: "1px solid #aeabab", 
            borderRadius: "5px", padding: "1rem 0.5rem 0.8rem 0.5rem", minHeight: "4.5rem" }}
          >
            <Col xs={12}></Col>
          </Row>
          ) : (
          infoQuestion.dependencies.activationConditionSets.map(
            (setItem, i) => (
              <Row
                key={i}
                style={{
                  margin: "0.5rem 0",
                  border: "1px solid #aeabab",
                  borderRadius: "5px",
                  padding: "0.5rem 0.5rem",
                }}
              >
                <Col xs={12} style={{ marginBottom: "0.5rem" }}>
                  <Row style={{ marginBottom: "0.3rem" }}>
                    <Col
                      xs={8}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <strong>群組{i + 1}</strong>
                    </Col>
                    <Col
                      xs={4}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        color="outline-info"
                        onClick={() => handleAddCondition(i)}
                        style={{ marginRight: "0.3rem" }}
                      >
                        +
                      </Button>
                      <Button
                        color="outline-danger"
                        onClick={() => handleRemoveActivationConditionSet(i)}
                      >
                        -
                      </Button>
                    </Col>
                  </Row>
                </Col>

                <Col xs={12}>
                  {setItem.conditions.map((cond, j) => (
                    <Row key={j} style={{ marginBottom: "0.3rem" }}>
                      <Col xs={8}>
                        <AutoResizeInput
                          value={cond}
                          onChange={(e) =>
                            handleConditionChange(i, j, e.target.value)
                          }
                          style={{ resize: "none" }}
                          //onBlur={handleSaveActivationConditions}
                        />
                      </Col>
                      <Col
                        xs={4}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          color="outline-danger"
                          onClick={() => handleRemoveCondition(i, j)}
                        >
                          -
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Row>
            ))
        )}
      </Col>
      {/* (copy the JSX from the original component) */}

      {/* --- AffectedAttributes --- */}
      <Col xs={12} md={6} xl={4} className="mb-3 mb-xl-0">
        <Row>
          <Col
            xs={12}
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            {/*<h3>Affected Attributes</h3>*/}
            <h3>影響屬性</h3>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                color="outline-info"
                onClick={handleAddAffectedAttribute}
                style={{ padding: "7px 8px", marginRight: "3px" }}
              >
                <i className="bx bx-plus" />
              </Button>
              <Button
                color="outline-success"
                onClick={handleSaveAffectedAttributes}
                style={{ padding: "7px 8px" }}
              >
                <i className="bx bx-check" />
              </Button>
            </div>
          </Col>
        </Row>

        {/* Show status */}
        <Row>
          {isSavingAffectedAttribute && <span style={{ color: "#f1b34c" }}>Saving...</span>}
          {!isSavingAffectedAttribute && 
            <span style={{ color: isSavedAffectedAttribute ? "#34c38f" : "#aeabab" }}>✔ Saved!</span>
          }
        </Row>

        {/* The child DnD component for affectedAttributes */}
        <Row
          style={{ margin: "0.5rem 0", border: "1px solid #aeabab", 
          borderRadius: "5px", padding: "1rem 0.5rem 0.8rem 0.5rem", minHeight: "4.5rem" }}
        >
          <Col xs={12}>
            <AffectedAttributesList
              affectedAttributes={infoQuestion.dependencies.affectedAttributes}
              onReorder={handleReorderAffectedAttributes}
              onRemoveItem={handleRemoveAffectedAttribute}
              onChangeItem={handleAffectedAttributeChange}
              onSave={handleSaveAffectedAttributes}
            />
          </Col>
        </Row>
      </Col>
      {/* (copy the JSX from the original component) */}

      {/* --- VerifyRuleAI --- */}
      <Col xs={12} md={6} xl={4}>
        <Row>
          <Col xs={12} className="d-flex justify-content-between">
            <h3>AI 驗證規則</h3>

            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                color="outline-info"
                onClick={handleAddVerifyRuleAISet}
                style={{ padding: "7px 8px", marginRight: "3px" }}
              >
                <i className="bx bx-plus" />
              </Button>
              <Button
                color="outline-success"
                onClick={handleSaveVerifyRuleAI}
                style={{ padding: "7px 8px" }}
              >
                <i className="bx bx-check" />
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          {isSavingVerifyRuleAI && <span style={{ color: "#f1b34c" }}>Saving...</span>}
          {!isSavingVerifyRuleAI && 
            <span style={{ color: isSavedVerifyRuleAI ? "#34c38f" : "#aeabab" }}>✔ Saved!</span>
          }
        </Row>

        <Row>
          <Col xs={12} style={{ margin: "0.5rem 0 0 0" }}>
            {prevQuestions && 
              <Select
                value={[
                  { value: '', label: '— 沒有需一併考慮的先前問題 —' },
                  ...prevQuestions
                    .map(q => ({
                      value: q._id,
                      label: `${q.order}. ${q.label}`
                    }))
                ].find(option => option.value === selectedReferencePrevQuestion)}
                onChange={(selected) => handleRefQuestionChange(infoQuestion._id, selected.value)}
                options={[
                  { value: '', label: '— No required question —' },
                  ...prevQuestions
                    .map(q => ({
                      value: q._id,
                      label: `${q.order}. ${q.label}`
                    }))
                ]}
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: 'white',
                    zIndex: 9999, // ensure it's above modal
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
                    color: '#495057',
                  }),
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: 'white',
                    color: 'black',
                  }),
                }}
              />
            }
          </Col>
        </Row>

        {infoQuestion.verifyRuleAI.length === 0 ? (
          <Row
            style={{ margin: "0.5rem 0", border: "1px solid #aeabab", 
            borderRadius: "5px", padding: "1rem 0.5rem 0.8rem 0.5rem", minHeight: "4.5rem" }}
          >
            <Col xs={12}></Col>
          </Row>
          ) : (
          infoQuestion.verifyRuleAI.map((setItem, m) => (
            <Row
              key={m}
              style={{
                margin: "0.5rem 0",
                border: "1px solid #aeabab",
                borderRadius: "5px",
                padding: "0.5rem 0.5rem",
              }}
            >
              <Col xs={12} style={{ marginBottom: "0.5rem" }}>
                <Row style={{ marginBottom: "0.3rem" }}>
                  <Col
                    xs={8}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <strong>驗證群組 {m + 1}</strong>
                  </Col>
                  <Col
                    xs={4}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      color="outline-info"
                      onClick={() => handleAddVerifyRuleAICondition(m)}
                      style={{ marginRight: "0.3rem" }}
                    >
                      +
                    </Button>
                    <Button
                      color="outline-danger"
                      onClick={() => handleRemoveVerifyRuleAISet(m)}
                    >
                      -
                    </Button>
                  </Col>
                </Row>
              </Col>

              <Col xs={12}>
                {setItem.conditions.map((cond, k) => (
                  <Row key={k} style={{ marginBottom: "0.3rem" }}>
                    <Col xs={8}>
                      <AutoResizeInput
                        value={cond}
                        onChange={(e) =>
                          handleVerifyRuleAIConditionChange(m, k, e.target.value)
                        }
                        style={{ resize: "none" }}
                        //onBlur={handleSaveVerifyRuleAI}
                      />
                    </Col>
                    <Col
                      xs={4}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        color="outline-danger"
                        onClick={() => handleRemoveVerifyRuleAICondition(m, k)}
                      >
                        -
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Col>
              
            </Row>
          ))
        )}

      </Col>
      {/* (copy the JSX from the original component) */}
    </Row>
  );
};

export default UserInfoQuestionDependencies;
